/**
 * Per-pack evidence contracts.
 *
 * Contract: docs/source-of-truth/worker-service-delivery-architecture.md
 * ("Evidence contract") and the matrix's core evidence discriminators.
 *
 * Two rules are enforced structurally here rather than left to reviewer
 * discipline:
 *
 * 1. **Workers record facts, not money.** A `MONEY_EVIDENCE` field captures an
 *    amount the worker actually saw on a receipt. It is permitted only in the
 *    two expense packs, and no pack may declare a claim item, unit, price,
 *    contribution, subsidy or funding-source field at all. Those are derived by
 *    the finance boundary from the catalogue and the provider agreement.
 * 2. **Clinical facts stay in their canonical module.** A pack references an
 *    observation, medication or wound record; it does not restate it as free
 *    JSON, which would create a second, drifting clinical store.
 */

import type { ServicePackCode } from './packs';
import { SERVICE_PACK_CODES, capturesReceiptEvidence } from './packs';

// ---------------------------------------------------------------------------
// Field types
// ---------------------------------------------------------------------------

export const EVIDENCE_FIELD_TYPES = [
  'BOOLEAN',
  'TEXT',
  'ENUM',
  'QUANTITY',
  'DURATION',
  'TIMESTAMP',
  /** A validated pointer into a canonical module record. */
  'REFERENCE',
  /** An amount observed on a receipt. Never a claim price. */
  'MONEY_EVIDENCE',
  'SIGNATURE',
] as const;

export type EvidenceFieldType = (typeof EVIDENCE_FIELD_TYPES)[number];

/**
 * Canonical modules an evidence reference may point at. A reference is
 * validated against the tenant, client and inclusion before it is accepted.
 */
export const EVIDENCE_REFERENCE_KINDS = [
  'OBSERVATION',
  'MEDICATION_EVENT',
  'WOUND_ASSESSMENT',
  'CONTINENCE_RECORD',
  'CASE_NOTE',
  'INCIDENT',
  'CLINICAL_ORDER',
  'DELEGATION',
  'WORK_ORDER',
  'CARE_PLAN_GOAL',
  'ASSESSMENT',
  'CONSENT',
  'AT_HM_REQUEST',
  'RECEIPT_DOCUMENT',
  'VOUCHER_BOOKING',
] as const;

export type EvidenceReferenceKind = (typeof EVIDENCE_REFERENCE_KINDS)[number];

export interface EvidenceFieldSpec {
  key: string;
  type: EvidenceFieldType;
  required: boolean;
  /**
   * Whether the profile explicitly permits `NOT_APPLICABLE_BY_DESIGN` for this
   * field. Never a blanket tenant bypass — it is declared per field.
   */
  allowNotApplicable?: boolean;
  referenceKind?: EvidenceReferenceKind;
  enumValues?: readonly string[];
}

export interface PackEvidenceSchema {
  pack: ServicePackCode;
  /** Bumped whenever fields change. Completed evidence keeps its own version. */
  version: number;
  fields: readonly EvidenceFieldSpec[];
}

// ---------------------------------------------------------------------------
// Forbidden field vocabulary
// ---------------------------------------------------------------------------

/**
 * The secondary tripwire. `APPROVED_EVIDENCE_FIELD_KEYS` is the primary control
 * and this runs over that list as well as over each schema, so a money field
 * cannot be smuggled in by registering it.
 *
 * What ultimately stops a worker authoring money is architectural: the
 * completion payload carries no finance rows at all, and the finance projection
 * derives item, unit, quantity, price, contribution and funding source
 * server-side from the catalogue, the inclusion and the provider agreement
 * (S2.4). Neither list here should be cited as the reason money is safe.
 *
 * Biased towards over-blocking, because a false positive costs one rename at
 * publication and a false negative is a worker authoring money.
 *
 * Field keys are matched by token, not by raw substring. Raw substring matching
 * failed both ways: `claimUnitPrice` slipped through for want of an underscore,
 * and `accurate_measurement` was rejected for containing the letters "rate".
 * Normalising to snake_case tokens fixes both, and publication additionally
 * rejects any key that is not already snake_case so the matcher never has to
 * guess at word boundaries.
 */
export function normaliseFieldKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function isSnakeCaseFieldKey(key: string): boolean {
  return key.length > 0 && key === normaliseFieldKey(key);
}

/**
 * Single tokens that only appear in a field key when a frontline actor would be
 * authoring finance semantics.
 *
 * Biased towards over-blocking. A false positive costs an author one rename at
 * publication; a false negative is a worker authoring money. A clinical measure
 * that genuinely needs the word "rate" should carry its unit instead, e.g.
 * `hydration_ml_per_hour`.
 */
const FORBIDDEN_TOKENS: readonly string[] = [
  'price',
  'rate',
  'contribution',
  'subsidy',
  'budget',
  'invoice',
  'billable',
  'chargeable',
  'tariff',
  'copayment',
  'payment',
  'remittance',
];

/**
 * Token pairs forbidden together but innocuous apart, so `claim_note` and
 * `item_description` stay legal while `claim_item` does not.
 *
 * Matched by co-occurrence anywhere in the key rather than adjacency:
 * `claim_line_item` and `claim_service_item` carry exactly the same meaning as
 * `claim_item` and previously walked past the gate.
 */
const FORBIDDEN_TOKEN_PAIRS: readonly (readonly [string, string])[] = [
  ['claim', 'item'],
  ['claim', 'unit'],
  ['claim', 'amount'],
  ['claim', 'category'],
  ['claim', 'line'],
  // Billing vocabulary in its own right; it does not need the word "claim"
  // beside it to be a billing shape.
  ['line', 'item'],
  ['billing', 'unit'],
  ['unit', 'type'],
  ['funding', 'source'],
  ['funding', 'pool'],
  ['funding', 'type'],
  ['deemed', 'delivery'],
  ['pay', 'run'],
];

/**
 * Crude singulariser. The vocabulary being matched is a small closed set of
 * finance nouns, so this only has to turn `prices`, `fees`, `amounts` and
 * `subsidies` into their singular forms. It is applied alongside the raw token,
 * never instead of it, so it can only ever match more.
 */
function singularise(token: string): string {
  if (token.endsWith('ies') && token.length > 4) return `${token.slice(0, -3)}y`;
  if (token.endsWith('sses')) return token.slice(0, -2);
  if (token.endsWith('s') && !token.endsWith('ss') && token.length > 3) return token.slice(0, -1);
  return token;
}

/**
 * Amount-bearing tokens. Legal inside an expense pack, where recording what a
 * receipt said is the point of the service; never legal outside one.
 *
 * `receipt` is deliberately absent: `receipt_confirmed` is a delivery fact, not
 * a money fact, and is legitimate on item packs.
 */
const AMOUNT_TOKENS: readonly string[] = ['amount', 'cost', 'fee', 'paid', 'payable', 'charge'];

/**
 * Both the raw and singular form of every token, so a plural cannot slip past a
 * singular entry in the vocabulary.
 */
function tokensOf(key: string): Set<string> {
  const raw = normaliseFieldKey(key).split('_').filter(Boolean);
  return new Set([...raw, ...raw.map(singularise)]);
}

export function fieldKeyAuthorsFinance(key: string): boolean {
  const tokens = tokensOf(key);
  if (FORBIDDEN_TOKENS.some((t) => tokens.has(t))) return true;
  return FORBIDDEN_TOKEN_PAIRS.some(([a, b]) => tokens.has(a) && tokens.has(b));
}

export function fieldKeyCarriesAmount(key: string): boolean {
  const tokens = tokensOf(key);
  return AMOUNT_TOKENS.some((t) => tokens.has(t));
}

/**
 * The reviewed evidence-field vocabulary.
 *
 * This allowlist is the primary control on what a worker can be asked to
 * record, and `fieldKeyAuthorsFinance` is a secondary tripwire over it.
 *
 * The two audit engines disagreed about the denylist in opposite directions:
 * one proved it under-blocks (`claimant_line_item`, `line_item`), the other
 * that it over-blocks (`payment` alone, `claim`+`line` colliding with
 * ordinary usage). Both are right, and neither is fixable by tuning the list:
 * a denylist tries to enumerate an unbounded set of bad names, and no
 * vocabulary can separate `line_item` the billing shape from `line_item` the
 * checklist row by spelling alone.
 *
 * The set of field keys is closed and source-controlled, so it can be
 * enumerated instead. An unregistered key fails publication, which means a new
 * worker-facing field cannot appear without landing in this list and being
 * read. The denylist still runs, and it runs over this list too, so a money
 * field cannot be smuggled in by adding it here.
 */
export const APPROVED_EVIDENCE_FIELD_KEYS: readonly string[] = [
  'action_owner',
  'activity_completed',
  'activity_duration',
  'activity_type',
  'activity_undertaken',
  'adverse_event_incident_ref',
  'adverse_event_occurred',
  'advice_given',
  'arrangement_outcome',
  'assistance_level',
  'assistance_provided',
  'at_hm_request_ref',
  'authorised_task_performed',
  'booking_ref',
  'carer_returned',
  'client_condition_summary',
  'client_engagement',
  'client_intake_observed',
  'client_response',
  'client_self_administered',
  'client_tolerance',
  'clinical_linkage_ref',
  'clinical_order_ref',
  'clinical_record_refs',
  'coercion_or_pressure_concern',
  'condition_change_observed',
  'consumables_used',
  'continence_record_ref',
  'defect_or_issue',
  'delegated_task_outcome',
  'delegation_ref',
  'delivery_outcome',
  'delivery_outcome_detail',
  'destination_and_return',
  'destination_reached',
  'diet_controls_followed',
  'discrepancy_detail',
  'discrepancy_observed',
  'escalated',
  'escalation_disposition',
  'exception_note',
  'expense_purpose',
  'follow_up_required',
  'goal_outcome_ref',
  'handover_at_destination',
  'hazard_or_defect_found',
  'household_hazard_observed',
  'incoming_handover_received',
  'incomplete_reason',
  'intervention_performed',
  'item_description',
  'legs_completed',
  'material_variance_note',
  'materials_used',
  'meal_prepared',
  'meals_delivered',
  'medication_event_ref',
  'milestone_stage',
  'no_contact_action_taken',
  'no_show_or_incident',
  'outcome',
  'outcome_measure',
  'outgoing_handover_given',
  'outstanding_actions',
  'participant_count_present',
  'parties_involved',
  'pickup_confirmed',
  'pickup_time',
  'prescription_ref',
  'product_issue',
  'provider_name',
  'quantity_supplied',
  'receipt_amount_observed',
  'receipt_confirmed',
  'receipt_ref',
  'recipient_confirmed',
  'recipient_or_supplier',
  'referral_ref',
  'refusal_or_partial_reason',
  'responsibility_window_confirmed',
  'session_activity',
  'session_exception',
  'shortfall_reason',
  'site_left_safe',
  'skin_or_condition_concern',
  'source_evidence_ref',
  'stage_outcome',
  'substitution_or_escalation',
  'substitution_reason',
  'supervisor_feedback_required',
  'supervisor_notified',
  'supplier_name',
  'support_provided',
  'supporting_document_ref',
  'task_status',
  'tasks_completed',
  'temperature_check_passed',
  'texture_modification_applied',
  'therapist_plan_ref',
  'transfer_method_used',
  'variance_from_delegation',
  'variance_reason',
  'work_incomplete_reason',
  'work_order_ref',
  'work_performed',
];

const APPROVED_FIELD_KEY_SET: ReadonlySet<string> = new Set(APPROVED_EVIDENCE_FIELD_KEYS);

export function isApprovedEvidenceFieldKey(key: string): boolean {
  return APPROVED_FIELD_KEY_SET.has(key);
}

// ---------------------------------------------------------------------------
// Common evidence
// ---------------------------------------------------------------------------

/**
 * Recorded for every completed occurrence regardless of pack. Times and the
 * responsible worker come from the server's own record of the occurrence, so
 * they are not restated as pack fields here.
 */
export const COMMON_EVIDENCE_FIELDS: readonly EvidenceFieldSpec[] = [
  {
    key: 'delivery_outcome',
    type: 'ENUM',
    required: true,
    enumValues: [
      'DELIVERED',
      'PARTIALLY_DELIVERED',
      'REFUSED',
      'NO_CONTACT',
      'UNSAFE_TO_PROCEED',
      'CANCELLED',
      'EXCEPTION',
    ],
  },
  { key: 'material_variance_note', type: 'TEXT', required: false },
  {
    key: 'escalation_disposition',
    type: 'ENUM',
    required: false,
    enumValues: ['NONE', 'SUPERVISOR_NOTIFIED', 'CLINICAL_ESCALATION', 'EMERGENCY_SERVICES'],
  },
];

// ---------------------------------------------------------------------------
// Pack schemas
// ---------------------------------------------------------------------------

const f = (
  key: string,
  type: EvidenceFieldType,
  required: boolean,
  extra: Omit<EvidenceFieldSpec, 'key' | 'type' | 'required'> = {},
): EvidenceFieldSpec => ({ key, type, required, ...extra });

const schema = (
  pack: ServicePackCode,
  fields: readonly EvidenceFieldSpec[],
): PackEvidenceSchema => ({ pack, version: 1, fields });

export const PACK_EVIDENCE_SCHEMAS: readonly PackEvidenceSchema[] = [
  schema('NURSING_CLINICAL', [
    f('clinical_order_ref', 'REFERENCE', true, { referenceKind: 'CLINICAL_ORDER' }),
    f('intervention_performed', 'ENUM', true, {
      enumValues: ['AS_ORDERED', 'MODIFIED', 'NOT_PERFORMED'],
    }),
    f('clinical_record_refs', 'REFERENCE', true, { referenceKind: 'OBSERVATION' }),
    f('client_response', 'TEXT', true),
    f('consumables_used', 'QUANTITY', false, { allowNotApplicable: true }),
    f('follow_up_required', 'BOOLEAN', true),
  ]),

  schema('DELEGATED_CLINICAL_SUPPORT', [
    f('delegation_ref', 'REFERENCE', true, { referenceKind: 'DELEGATION' }),
    f('delegated_task_outcome', 'ENUM', true, {
      enumValues: ['COMPLETED', 'PARTIAL', 'NOT_COMPLETED'],
    }),
    f('variance_from_delegation', 'TEXT', false),
    f('supervisor_notified', 'BOOLEAN', true),
    f('follow_up_required', 'BOOLEAN', true),
  ]),

  schema('CLINICAL_ITEM', [
    f('item_description', 'TEXT', true),
    f('quantity_supplied', 'QUANTITY', true),
    f('recipient_confirmed', 'BOOLEAN', true),
    f('clinical_linkage_ref', 'REFERENCE', false, { referenceKind: 'CLINICAL_ORDER' }),
    f('shortfall_reason', 'TEXT', false),
  ]),

  schema('ALLIED_PROFESSIONAL', [
    f('referral_ref', 'REFERENCE', true, { referenceKind: 'CLINICAL_ORDER' }),
    f('activity_type', 'ENUM', true, { enumValues: ['ASSESSMENT', 'INTERVENTION', 'REVIEW'] }),
    f('outcome_measure', 'TEXT', true),
    f('advice_given', 'TEXT', false),
    // Added by ruling: this pack previously had no way to record an adverse
    // event at all, so the invariant meant to cover it was inert.
    //
    // Optional, unlike THERAPEUTIC_PROFESSIONAL where it has always been
    // required. Making it required here would fail `required_field_missing` on
    // every ALLIED_PROFESSIONAL bundle ever stored, and no migration can repair
    // that: defaulting the flag to false asserts a clinical negative nobody was
    // asked for, which the evidence-migration contract explicitly forbids. The
    // invariant fires on an explicit `true`, so an absent value on historical
    // evidence never fires falsely. New captures supply it because the capture
    // surface asks; the past stays honestly silent.
    f('adverse_event_occurred', 'BOOLEAN', false),
    f('adverse_event_incident_ref', 'REFERENCE', false, { referenceKind: 'INCIDENT' }),
    f('follow_up_required', 'BOOLEAN', true),
  ]),

  schema('ALLIED_ASSISTANT', [
    f('therapist_plan_ref', 'REFERENCE', true, { referenceKind: 'CLINICAL_ORDER' }),
    f('delegation_ref', 'REFERENCE', true, { referenceKind: 'DELEGATION' }),
    f('activity_completed', 'ENUM', true, { enumValues: ['AS_PLANNED', 'MODIFIED', 'NOT_DONE'] }),
    f('client_tolerance', 'TEXT', true),
    f('supervisor_feedback_required', 'BOOLEAN', true),
  ]),

  // Therapeutic services have no canonical clinical module of their own, so
  // their intervention and response are legitimately narrative. An adverse
  // event is different: it must be able to point at the incident record rather
  // than sit in free text where nothing downstream can act on it. The
  // "adverse event implies an incident reference" rule is a cross-field
  // invariant enforced at completion (S2.1), not expressible as a field flag.
  schema('THERAPEUTIC_PROFESSIONAL', [
    f('intervention_performed', 'TEXT', true),
    f('client_response', 'TEXT', true),
    f('adverse_event_occurred', 'BOOLEAN', true),
    f('adverse_event_incident_ref', 'REFERENCE', false, { referenceKind: 'INCIDENT' }),
    f('advice_given', 'TEXT', false),
    f('follow_up_required', 'BOOLEAN', true),
  ]),

  schema('NUTRITION_ITEM', [
    f('prescription_ref', 'REFERENCE', true, { referenceKind: 'CLINICAL_ORDER' }),
    f('item_description', 'TEXT', true),
    f('quantity_supplied', 'QUANTITY', true),
    f('receipt_confirmed', 'BOOLEAN', true),
    f('substitution_reason', 'TEXT', false),
  ]),

  schema('CARE_MANAGEMENT', [
    f('activity_type', 'ENUM', true, {
      enumValues: [
        'CARE_PLAN_REVIEW',
        'COORDINATION',
        'CLIENT_CONTACT',
        'FAMILY_CONTACT',
        'REFERRAL',
        'ADVOCACY',
        'MONITORING',
      ],
    }),
    f('parties_involved', 'TEXT', true),
    f('activity_duration', 'DURATION', true),
    f('outcome', 'TEXT', true),
    f('action_owner', 'TEXT', false),
    f('source_evidence_ref', 'REFERENCE', false, { referenceKind: 'CASE_NOTE' }),
    f('follow_up_required', 'BOOLEAN', true),
  ]),

  schema('PERSONAL_CARE_ADL', [
    f('tasks_completed', 'TEXT', true),
    f('assistance_level', 'ENUM', true, {
      enumValues: ['INDEPENDENT', 'SUPERVISION', 'MINIMAL', 'MODERATE', 'FULL'],
    }),
    f('refusal_or_partial_reason', 'TEXT', false),
    f('condition_change_observed', 'BOOLEAN', true),
    f('transfer_method_used', 'TEXT', false, { allowNotApplicable: true }),
  ]),

  schema('MEDICATION_SELF_ADMIN_ASSIST', [
    // Assistance only. Administration is a different authority and service.
    f('assistance_provided', 'ENUM', true, {
      enumValues: ['PROMPTED', 'OPENED_PACKAGING', 'POSITIONED', 'OBSERVED', 'NONE'],
    }),
    f('client_self_administered', 'BOOLEAN', true),
    f('discrepancy_observed', 'BOOLEAN', true),
    f('discrepancy_detail', 'TEXT', false),
    f('medication_event_ref', 'REFERENCE', false, { referenceKind: 'MEDICATION_EVENT' }),
    f('escalated', 'BOOLEAN', true),
  ]),

  schema('CONTINENCE_NONCLINICAL', [
    f('support_provided', 'TEXT', true),
    f('skin_or_condition_concern', 'BOOLEAN', true),
    f('product_issue', 'TEXT', false),
    f('continence_record_ref', 'REFERENCE', false, { referenceKind: 'CONTINENCE_RECORD' }),
    f('escalated', 'BOOLEAN', true),
  ]),

  schema('SOCIAL_GROUP', [
    f('session_activity', 'TEXT', true),
    // Per-participant attendance lives on the participant child record, not
    // here, so the session is never cloned per client.
    f('participant_count_present', 'QUANTITY', true),
    f('session_exception', 'TEXT', false),
  ]),

  schema('SOCIAL_INDIVIDUAL', [
    f('activity_undertaken', 'TEXT', true),
    f('client_engagement', 'ENUM', true, {
      enumValues: ['ENGAGED', 'PARTIALLY_ENGAGED', 'DECLINED'],
    }),
    f('goal_outcome_ref', 'REFERENCE', false, { referenceKind: 'CARE_PLAN_GOAL' }),
    f('destination_and_return', 'TEXT', false, { allowNotApplicable: true }),
    f('variance_reason', 'TEXT', false),
  ]),

  schema('PERSONAL_AFFAIRS', [
    f('authorised_task_performed', 'TEXT', true),
    f('parties_involved', 'TEXT', true),
    f('outcome', 'TEXT', true),
    f('coercion_or_pressure_concern', 'BOOLEAN', true),
    f('follow_up_required', 'BOOLEAN', true),
  ]),

  schema('PERSONAL_AFFAIRS_EXPENSE', [
    f('receipt_ref', 'REFERENCE', true, { referenceKind: 'RECEIPT_DOCUMENT' }),
    f('receipt_amount_observed', 'MONEY_EVIDENCE', true),
    f('expense_purpose', 'TEXT', true),
    f('recipient_or_supplier', 'TEXT', true),
    f('exception_note', 'TEXT', false),
  ]),

  schema('RESPITE', [
    f('incoming_handover_received', 'BOOLEAN', true),
    f('responsibility_window_confirmed', 'BOOLEAN', true),
    f('client_condition_summary', 'TEXT', true),
    f('outgoing_handover_given', 'BOOLEAN', true),
    f('carer_returned', 'BOOLEAN', true),
    f('outstanding_actions', 'TEXT', false),
  ]),

  schema('TRANSPORT_DIRECT', [
    f('pickup_confirmed', 'BOOLEAN', true),
    f('pickup_time', 'TIMESTAMP', true),
    f('legs_completed', 'QUANTITY', true),
    f('destination_reached', 'BOOLEAN', true),
    f('handover_at_destination', 'TEXT', false, { allowNotApplicable: true }),
    f('no_show_or_incident', 'TEXT', false),
  ]),

  schema('TRANSPORT_INDIRECT', [
    f('booking_ref', 'REFERENCE', true, { referenceKind: 'VOUCHER_BOOKING' }),
    f('provider_name', 'TEXT', true),
    f('arrangement_outcome', 'ENUM', true, {
      enumValues: ['REDEEMED', 'ISSUED_UNUSED', 'CANCELLED', 'FAILED'],
    }),
    f('exception_note', 'TEXT', false),
  ]),

  schema('AT_HM_LIFECYCLE', [
    f('at_hm_request_ref', 'REFERENCE', true, { referenceKind: 'AT_HM_REQUEST' }),
    f('milestone_stage', 'ENUM', true, {
      enumValues: [
        'NEED_IDENTIFIED',
        'ASSESSMENT',
        'PRESCRIPTION',
        'APPROVAL',
        'PROCUREMENT',
        'INSTALLATION',
        'TRAINING',
        'REVIEW',
      ],
    }),
    f('stage_outcome', 'ENUM', true, { enumValues: ['COMPLETED', 'BLOCKED', 'DEFERRED'] }),
    f('supporting_document_ref', 'REFERENCE', false, { referenceKind: 'RECEIPT_DOCUMENT' }),
    f('defect_or_issue', 'TEXT', false),
  ]),

  schema('DOMESTIC', [
    f('tasks_completed', 'TEXT', true),
    f('task_status', 'ENUM', true, { enumValues: ['COMPLETED', 'PARTIAL', 'REFUSED'] }),
    f('incomplete_reason', 'TEXT', false),
    f('household_hazard_observed', 'BOOLEAN', true),
    f('site_left_safe', 'BOOLEAN', true),
  ]),

  schema('MAINTENANCE_WORK', [
    f('work_order_ref', 'REFERENCE', true, { referenceKind: 'WORK_ORDER' }),
    f('work_performed', 'TEXT', true),
    f('materials_used', 'TEXT', false, { allowNotApplicable: true }),
    f('hazard_or_defect_found', 'BOOLEAN', true),
    f('work_incomplete_reason', 'TEXT', false),
    f('site_left_safe', 'BOOLEAN', true),
  ]),

  schema('MAINTENANCE_EXPENSE', [
    f('work_order_ref', 'REFERENCE', true, { referenceKind: 'WORK_ORDER' }),
    f('receipt_ref', 'REFERENCE', true, { referenceKind: 'RECEIPT_DOCUMENT' }),
    f('receipt_amount_observed', 'MONEY_EVIDENCE', true),
    f('supplier_name', 'TEXT', true),
    f('expense_purpose', 'TEXT', true),
    f('exception_note', 'TEXT', false),
  ]),

  schema('MEAL_PREPARATION', [
    f('meal_prepared', 'TEXT', true),
    f('diet_controls_followed', 'BOOLEAN', true, { allowNotApplicable: true }),
    f('texture_modification_applied', 'TEXT', false, { allowNotApplicable: true }),
    // Three absence states that must not be collapsed, per principle 6:
    // NONE          — the client ate nothing. A real observation.
    // NOT_OBSERVED  — the worker did not see the client eat. No observation.
    // NOT_APPLICABLE_BY_DESIGN — intake tracking is not part of this client's
    //                 plan, so the question was never asked.
    f('client_intake_observed', 'ENUM', false, {
      enumValues: ['ALL', 'MOST', 'SOME', 'NONE', 'NOT_OBSERVED'],
      allowNotApplicable: true,
    }),
    f('substitution_or_escalation', 'TEXT', false),
  ]),

  schema('MEAL_DELIVERY', [
    f('meals_delivered', 'QUANTITY', true),
    f('temperature_check_passed', 'BOOLEAN', false, { allowNotApplicable: true }),
    f('delivery_outcome_detail', 'ENUM', true, {
      enumValues: ['HANDED_TO_CLIENT', 'LEFT_AS_AGREED', 'NO_CONTACT', 'NOT_DELIVERED'],
    }),
    f('no_contact_action_taken', 'TEXT', false),
  ]),
];

const SCHEMA_BY_PACK: ReadonlyMap<ServicePackCode, PackEvidenceSchema> = new Map(
  PACK_EVIDENCE_SCHEMAS.map((s) => [s.pack, s]),
);

export function evidenceSchemaForPack(pack: ServicePackCode): PackEvidenceSchema | null {
  return SCHEMA_BY_PACK.get(pack) ?? null;
}

/**
 * Throws rather than returning an empty list for an unknown pack.
 *
 * Callers treat this as the completion contract, so an empty array would mean
 * "nothing is required" and would let an occurrence complete with no evidence
 * at all — including dropping the common `delivery_outcome`. A missing schema
 * is a publication error and must fail closed, not silently authorise an empty
 * scope.
 */
export function requiredFieldKeysForPack(pack: ServicePackCode): readonly string[] {
  const s = evidenceSchemaForPack(pack);
  if (!s) {
    throw new Error(`missing_evidence_schema:${pack}`);
  }
  return [
    ...COMMON_EVIDENCE_FIELDS.filter((field) => field.required).map((field) => field.key),
    ...s.fields.filter((field) => field.required).map((field) => field.key),
  ];
}

// ---------------------------------------------------------------------------
// Publication gates
// ---------------------------------------------------------------------------

export interface EvidenceSchemaIssue {
  pack: ServicePackCode | null;
  field?: string;
  issue: string;
  detail?: string;
}

/**
 * Proves every pack has a schema, that no schema lets a worker author finance
 * semantics, and that money evidence appears only where an expense is the
 * point of the service.
 */
export function findEvidenceSchemaIssues(
  schemas: readonly PackEvidenceSchema[] = PACK_EVIDENCE_SCHEMAS,
): EvidenceSchemaIssue[] {
  const issues: EvidenceSchemaIssue[] = [];
  const byPack = new Set(schemas.map((s) => s.pack));

  for (const pack of SERVICE_PACK_CODES) {
    if (!byPack.has(pack)) {
      issues.push({ pack, issue: 'missing_evidence_schema' });
    }
  }

  const seen = new Set<ServicePackCode>();
  for (const s of schemas) {
    if (seen.has(s.pack)) issues.push({ pack: s.pack, issue: 'duplicate_evidence_schema' });
    seen.add(s.pack);

    if (s.fields.length === 0) {
      issues.push({ pack: s.pack, issue: 'schema_declares_no_fields' });
    }

    const keys = new Set<string>();
    const commonKeys = new Set(COMMON_EVIDENCE_FIELDS.map((field) => field.key));

    for (const field of s.fields) {
      if (keys.has(field.key)) {
        issues.push({ pack: s.pack, field: field.key, issue: 'duplicate_field_key' });
      }
      keys.add(field.key);

      if (commonKeys.has(field.key)) {
        issues.push({ pack: s.pack, field: field.key, issue: 'field_shadows_common_evidence' });
      }

      if (!isSnakeCaseFieldKey(field.key)) {
        // Keeps the finance matcher from having to guess word boundaries.
        issues.push({ pack: s.pack, field: field.key, issue: 'field_key_not_snake_case' });
      }

      // The primary control. An unregistered key cannot reach a worker, so a
      // new worker-facing field has to land in the reviewed vocabulary first.
      if (!isApprovedEvidenceFieldKey(field.key)) {
        issues.push({
          pack: s.pack,
          field: field.key,
          issue: 'field_key_not_in_approved_vocabulary',
        });
      }

      if (fieldKeyAuthorsFinance(field.key)) {
        issues.push({
          pack: s.pack,
          field: field.key,
          issue: 'field_authors_finance_semantics',
        });
      }

      // Checked on the key as well as the declared type. Typing an amount as a
      // QUANTITY rather than MONEY_EVIDENCE would otherwise carry the same
      // money semantics past the type check.
      if (!capturesReceiptEvidence(s.pack)) {
        if (field.type === 'MONEY_EVIDENCE') {
          issues.push({
            pack: s.pack,
            field: field.key,
            issue: 'money_evidence_outside_expense_pack',
          });
        }
        if (fieldKeyCarriesAmount(field.key)) {
          issues.push({
            pack: s.pack,
            field: field.key,
            issue: 'amount_field_outside_expense_pack',
          });
        }
      }

      if (field.type === 'REFERENCE' && !field.referenceKind) {
        issues.push({ pack: s.pack, field: field.key, issue: 'reference_without_kind' });
      }

      if (field.type === 'ENUM' && (!field.enumValues || field.enumValues.length === 0)) {
        issues.push({ pack: s.pack, field: field.key, issue: 'enum_without_values' });
      }

      if (field.required && field.type === 'TEXT' && field.allowNotApplicable) {
        // Deliberately TEXT-only, not a rule about required fields generally.
        // For a boolean or enum, NOT_APPLICABLE_BY_DESIGN is a meaningful third
        // state distinct from true/false — a client with no diet controls is
        // not the same as one whose controls were ignored. For free text,
        // "N/A" and "left blank" are indistinguishable once written, so a
        // waivable required text field just hides a real gap.
        issues.push({
          pack: s.pack,
          field: field.key,
          issue: 'required_text_field_allows_not_applicable',
        });
      }
    }
  }

  return issues;
}
